/**
 * Centralized API Client for EVCS Backend
 * Handles all HTTP requests with consistent error handling
 */

import axios from 'axios';
import { Capacitor } from '@capacitor/core';

// Detect whether we're running inside a Capacitor native app (iOS / Android).
// When native, relative URLs don't resolve — we must use an absolute backend URL.
const isNative = Capacitor.isNativePlatform();

if (isNative && !process.env.REACT_APP_API_BASE_URL) {
  // eslint-disable-next-line no-console
  console.warn('⚠️ [EVCS] Running in native app without REACT_APP_API_BASE_URL set. ' +
    'API calls will fail. See frontend/.env.capacitor.example.');
}

// Web dev:    falls back to "/evcs/api" (proxied by setupProxy.js)
// Native app: REACT_APP_API_BASE_URL must be set at build time (see cap:build script)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || (isNative ? '' : '/evcs/api');

// Create axios instance with defaults
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 seconds
});

// Request interceptor (for future: add auth tokens)
apiClient.interceptors.request.use(
  (config) => {
    // Future: Add JWT token from localStorage
    // const token = localStorage.getItem('auth_token');
    // if (token) {
    //   config.headers.Authorization = `Bearer ${token}`;
    // }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor (for consistent error handling)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle network errors
    if (!error.response) {
      console.error('❌ Network error:', error.message);
      return Promise.reject({
        type: 'network',
        message: 'Network error. Please check your connection.',
        originalError: error,
      });
    }

    // Handle HTTP errors
    const { status, data } = error.response;
    console.error(`❌ API Error (${status}):`, data);

    return Promise.reject({
      type: 'http',
      status,
      message: data.error || data.message || 'An error occurred',
      data,
      originalError: error,
    });
  }
);

// ============== GAME PROGRESS API ==============

/**
 * Load user's game progress from S3
 * @param {string} userId - Google user ID (sub claim)
 * @param {string} userEmail - User's email (optional, for beta testing)
 * @returns {Promise<Object>} Progress data
 */
export const loadProgress = async (userId, userEmail = null) => {
  const params = userEmail ? { email: userEmail } : {};
  const response = await apiClient.get(`/user/${userId}/progress`, { params });
  return response.data;
};

/**
 * Save user's game progress to S3
 * @param {string} userId - Google user ID
 * @param {Object} progressData - Game progress object
 * @param {string} userEmail - User's email (optional, for beta testing)
 * @returns {Promise<Object>} Success response
 */
export const saveProgress = async (userId, progressData, userEmail = null) => {
  const payload = { ...progressData };
  if (userEmail) {
    payload.user_email = userEmail;
  }
  const response = await apiClient.post(`/user/${userId}/progress`, payload);
  return response.data;
};

// ============== USER PROFILE API ==============

/**
 * Load user profile metadata
 * @param {string} userId - Google user ID
 * @param {string} userEmail - User's email (optional, for beta testing)
 * @returns {Promise<Object>} Profile data
 */
export const loadProfile = async (userId, userEmail = null) => {
  const params = userEmail ? { email: userEmail } : {};
  const response = await apiClient.get(`/user/${userId}/profile`, { params });
  return response.data;
};

/**
 * Save user profile metadata
 * @param {string} userId - Google user ID
 * @param {Object} profileData - Profile object
 * @param {string} userEmail - User's email (optional, for beta testing)
 * @returns {Promise<Object>} Success response
 */
export const saveProfile = async (userId, profileData, userEmail = null) => {
  const payload = { ...profileData };
  if (userEmail) {
    payload.user_email = userEmail;
  }
  const response = await apiClient.post(`/user/${userId}/profile`, payload);
  return response.data;
};

// ============== RECORDINGS API ==============

/**
 * List user's recordings
 * @param {string} userId - Google user ID
 * @param {number} limit - Max number of recordings to return
 * @returns {Promise<Object>} Recordings list
 */
export const listRecordings = async (userId, limit = 100) => {
  const response = await apiClient.get(`/user/${userId}/recordings`, {
    params: { limit },
  });
  return response.data;
};

/**
 * Get user storage statistics
 * @param {string} userId - Google user ID
 * @returns {Promise<Object>} Storage stats
 */
export const getStorageStats = async (userId) => {
  const response = await apiClient.get(`/user/${userId}/stats`);
  return response.data;
};

// ============== UTILITY FUNCTIONS ==============

/**
 * Check if API is reachable
 * @returns {Promise<boolean>} True if API is up
 */
export const healthCheck = async () => {
  try {
    // Try any lightweight endpoint
    await apiClient.get('/health', { timeout: 3000 });
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Generic error handler for UI components
 * @param {Error} error - Error from API call
 * @returns {string} User-friendly error message
 */
/**
 * Upload a session pitch log and optional calibration audio to S3 (via backend).
 * Uses fetch + FormData rather than axios to avoid multipart encoding quirks
 * on iOS WKWebView.
 *
 * @param {string}  userId     - Google user ID (sub)
 * @param {Object|null} logData    - Pitch log JSON (null when only uploading audio)
 * @param {Blob|null}   calibAudio - Calibration audio webm blob (optional)
 * @param {string|null} userEmail  - User email (for readable S3 folder)
 */
export const uploadSessionLog = async (userId, logData, calibAudio = null, userEmail = null) => {
  const base = process.env.REACT_APP_API_BASE_URL || (isNative ? '' : '/evcs/api');

  const form = new FormData();
  if (logData) {
    form.append(
      'log',
      new Blob([JSON.stringify(logData)], { type: 'application/json' }),
      'session-log.json',
    );
  }
  if (userEmail) form.append('user_email', userEmail);
  if (calibAudio && calibAudio.size > 1000) {
    form.append('calib_audio', calibAudio, 'calibration.webm');
  }

  const resp = await fetch(`${base}/user/${userId}/session-log`, {
    method: 'POST',
    body: form,
  });

  if (!resp.ok) {
    const text = await resp.text().catch(() => resp.status.toString());
    throw new Error(`Session log upload failed (${resp.status}): ${text}`);
  }
  return resp.json();
};

export const getErrorMessage = (error) => {
  if (error.type === 'network') {
    return '🔌 Connection lost. Changes will sync when you\'re back online.';
  }

  if (error.type === 'http') {
    if (error.status === 401) {
      return '🔒 Please sign in again to sync your progress.';
    }
    if (error.status === 404) {
      return 'ℹ️ No saved progress found. Starting fresh!';
    }
    if (error.status >= 500) {
      return '⚠️ Server error. We\'ll retry shortly.';
    }
    return error.message || 'Something went wrong';
  }

  return '❌ Unexpected error occurred';
};

export default apiClient;
